import WebApp from '@twa-dev/sdk';
import api from '../api/axios';

const API_URL = process.env.REACT_APP_API_URL;

// Функция для установки токена в заголовки
const setAuthToken = (token) => {
	if (token) {
		api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	} else {
		delete api.defaults.headers.common['Authorization'];
	}
};

// Добавляем интерцептор для автоматического обновления токена
api.interceptors.response.use(
	response => response,
	async error => {
		if (error.response?.status === 401) {
			// Если токен истек, пробуем обновить его
			try {
				const response = await api.post('/auth', {
					initData: WebApp.initData
				});
				const data = response.data || response;
				const { token } = data;
				if (token) {
					setAuthToken(token);
					// Повторяем запрос с новым токеном
					error.config.headers['Authorization'] = `Bearer ${token}`;
					return api(error.config);
				}
			} catch (refreshError) {
				console.error('Ошибка при обновлении токена:', refreshError);
			}
		}
		return Promise.reject(error);
	}
);

// Добавляем интерцептор для установки токена в заголовки
api.interceptors.request.use(
	config => {
		// Берём токен из zustand-стора
		try {
			const useUserStore = require('../stores/userStore').default;
			const token = useUserStore.getState().token;
			if (token) {
				config.headers['Authorization'] = `Bearer ${token}`;
			}
		} catch (e) {
			// fallback, если zustand не доступен
		}
		return config;
	},
	error => {
		return Promise.reject(error);
	}
);

export const apiService = {
	// Авторизация пользователя
	auth: async () => {
		try {
			const response = await api.post('/auth', {
				initData: WebApp.initData
			});

			console.log('Полный ответ от auth:', response);

			// Проверяем структуру ответа
			const data = response.data || response;

			console.log('Полученный токен:', data.token);

			if (data.token) {
				setAuthToken(data.token);
			}

			const { token, ...user } = data;

			return { token, user };
		} catch (error) {
			console.error('Ошибка при авторизации:', error);
			throw error;
		}
	},

	// Получение данных пользователя
	getMe: async () => {
		try {
			const response = await api.get('/me');

			console.log('Полный ответ от getMe:', response);

			// Проверяем структуру ответа
			const data = response.data || response;

			// Проверяем, что ответ содержит нужные поля
			if (data && data.status === 'success' && data.data) {
				return { status: 'success', data: data.data };
			}

			// Если ответ не содержит нужных полей, но содержит данные пользователя напрямую
			if (data && data.telegramId) {
				return { status: 'success', data };
			}

			return { status: 'error', error: 'Неверный формат ответа от сервера' };
		} catch (error) {
			console.error('Ошибка при получении данных пользователя:', error);
			if (error.response && (error.response.status === 401 || error.response.status === 404)) {
				delete api.defaults.headers.common['Authorization'];
			}
			return { status: 'error', error: error.message || 'Ошибка при получении профиля' };
		}
	},

	// Получение trade URL пользователя
	getTradeUrl: async () => {
		try {
			const response = await api.get('/user/trade-url');
			console.log('Raw API response for trade URL:', response);
			return response;
		} catch (error) {
			console.error('Ошибка при получении trade URL:', error);
			throw error;
		}
	},

	// Обновление trade URL пользователя
	updateTradeUrl: async (tradeUrl) => {
		try {
			const response = await api.put('/user/trade-url', {
				tradeUrl
			});
			console.log('Raw API response for update trade URL:', response);
			return response;
		} catch (error) {
			console.error('Ошибка при обновлении trade URL:', error);
			throw error;
		}
	},

	// Выход из системы
	logout: () => {
		setAuthToken(null);
	},

	getCases: async () => {
		try {
			const response = await api.get('/cases');
			return response.data;
		} catch (error) {
			console.error('Ошибка при получении кейсов:', error);
			throw error;
		}
	},

	getCaseById: async (id) => {
		try {
			const response = await api.get(`/cases/${id}`);
			return response;
		} catch (error) {
			console.error('Ошибка при получении кейса:', error);
			throw error;
		}
	},

	openCase: async (caseId, telegramId) => {
		try {
			const response = await api.post(`/cases/${caseId}/open`, { telegramId });
			console.log('Raw response from openCase:', response);
			return response;
		} catch (error) {
			console.error('Ошибка при открытии кейса:', error);
			throw error;
		}
	},

	getReferralStats: async () => {
		try {
			const response = await api.get('/user/referrals');
			return response.data;
		} catch (error) {
			console.error('Error getting referral stats:', error);
			throw error;
		}
	},

	// Получение предметов пользователя
	getUserItems: async () => {
		try {
			const response = await api.get('/user/items');
			console.log('Raw API response for user items:', response);

			// Проверяем структуру ответа
			if (response.data && response.data.data) {
				console.log('Returning response.data.data:', response.data.data);
				return response.data.data;
			} else if (response.data) {
				console.log('Returning response.data:', response.data);
				return response.data;
			} else {
				console.log('Returning full response:', response);
				return response;
			}
		} catch (error) {
			console.error('Ошибка при получении предметов пользователя:', error);
			throw error;
		}
	},

	getPackages: async () => {
		try {
			const response = await api.get('/payments/packages');
			return response.data;
		} catch (error) {
			console.error('Error fetching packages:', error);
			throw error;
		}
	},

	// Создание ссылки на инвойс
	createInvoiceLink: async (pack_id) => {
		try {
			console.log('Creating invoice link for pack ID:', pack_id);
			const telegramId = WebApp.initDataUnsafe?.user?.id;

			const response = await api.post('/payments/create-invoice-link', {
				pack_id,
				telegram_id: telegramId,
				init_data: WebApp.initData // Добавляем init_data для верификации на бэкенде
			});

			console.log('Invoice link response:', response);

			// Возвращаем ответ в исходном виде, обработка формата будет в компоненте
			return response.data;
		} catch (error) {
			console.error('Error creating invoice link:', {
				error: error.message,
				response: error.response?.data,
				status: error.response?.status
			});
			throw error;
		}
	},

	getWeaponById: async (weaponId) => {
		try {
			console.log('Making API request for weapon:', weaponId);
			const response = await api.get(`/user/item/${weaponId}`);
			console.log('Raw API response:', response);

			if (response.success && response.item) {
				console.log('Returning item data:', response.item);
				return response.item;
			} else {
				console.error('Invalid response format:', response);
				throw new Error('Invalid response format');
			}
		} catch (error) {
			console.error('Error in getWeaponById:', error);
			throw error;
		}
	},

	// Получение списка рефералов
	getReferrals: async () => {
		try {
			const response = await api.get('/user/referrals');
			return response;
		} catch (error) {
			console.error('Error getting referrals list:', error);
			throw error;
		}
	},

	getTransactionStatus: async (transactionId, options = {}) => {
		try {
			const response = await api.get(`/payments/transaction-status/${transactionId}`, options);
			return response;
		} catch (error) {
			console.error('Ошибка при получении статуса транзакции:', error);
			throw error;
		}
	},

	// Создание новой продажи
	createSale: async (itemId) => {
		try {
			const response = await api.post('/sales/sell', {
				item_id: itemId
			});
			return response.data;
		} catch (error) {
			console.error('Ошибка при создании продажи:', error);
			throw error;
		}
	},

	// Запрос на вывод предмета
	requestWithdrawal: async (itemId, tradeUrl) => {
		try {
			const response = await api.post(`/user/item/${itemId}/request-withdrawal`, {
				trade_url: tradeUrl
			});
			return response.data;
		} catch (error) {
			console.error('Ошибка при запросе на вывод предмета:', error);
			throw error;
		}
	},

	// Получение ежедневной награды: проверка доступности
	checkDailyReward: async () => {
		try {
			const response = await api.get('/daily-reward/check');
			return response.data;
		} catch (error) {
			console.error('Ошибка при проверке ежедневной награды:', error);
			throw error;
		}
	},

	// Получение ежедневной награды: получение
	claimDailyReward: async () => {
		try {
			const response = await api.post('/daily-reward/claim');
			return response.data;
		} catch (error) {
			console.error('Ошибка при получении ежедневной награды:', error);
			throw error;
		}
	},

	// Проверка подписки на канал
	checkSubscription: async () => {
		try {
			const response = await api.post('/subscription/check-subscription');
			return response.data;
		} catch (error) {
			console.error('Ошибка при проверке подписки:', error);
			throw error;
		}
	},

	// Получить информацию о серии ежедневных наград
	getStreak: async () => {
		const response = await api.get('/daily-reward/streak');
		return response;
	},

	// Получить бонус за серию
	claimStreakBonus: async () => {
		const response = await api.post('/daily-reward/claim-streak-bonus');
		return response.data;
	},

	// Сохранение подготовленного инлайн-сообщения для шаринга
	savePreparedInlineMessage: async ({ item_id }) => {
		try {
			const response = await api.post('/savePreparedInlineMessage', { item_id });
			return response;
		} catch (error) {
			console.error('Ошибка при сохранении prepared inline message:', error);
			throw error;
		}
	}
}; 